// Decision helpers that are not tied to React, so the harness can exercise
// them:
//   1. resolveDecisionBatch / skippedNote — what a bulk approve or deny should
//      actually write at the moment the reviewer confirms it.
//   2. loadDenialDecisionsSince — the denial/archive rows behind the
//      dashboard's "Why invoices are denied" panel.
import { getClient } from '../api/client'
import type { Decision } from './types'
import { DENIAL_OUTCOMES } from './metrics'
import type { DecisionOutcome } from './constants'

// ---- What a bulk decision should write at confirm ---------------------------
// A bulk approve/deny snapshots the checked ids when the dialog opens, and the
// queue keeps reloading behind it: by the time the reviewer confirms, a row may
// have left the queue, been put on hold, or fallen out of their authority. The
// broker rejects such a decision — after a 32-second fence wait and a rejected
// audit row — so the screens re-resolve the batch here at confirm instead.
export interface DecisionBatch<T> {
  /** Rows still present and still actionable — the ones to write. */
  eligible: T[]
  /** Ids no longer in the queue (already handled elsewhere). */
  gone: string[]
  /** Ids still queued but now on hold or no longer decidable by this user. */
  blocked: string[]
}

export function resolveDecisionBatch<T>(
  ids: readonly string[],
  lookup: (id: string) => T | undefined,
  actionable: (row: T) => boolean,
): DecisionBatch<T> {
  const eligible: T[] = []
  const gone: string[] = []
  const blocked: string[] = []
  for (const id of ids) {
    const row = lookup(id)
    if (!row) gone.push(id)
    else if (!actionable(row)) blocked.push(id)
    else eligible.push(row)
  }
  return { eligible, gone, blocked }
}

/** One sentence naming what was dropped from a batch, or undefined if none was. */
export function skippedNote(batch: DecisionBatch<unknown>): string | undefined {
  const parts: string[] = []
  if (batch.gone.length) parts.push(`${batch.gone.length} already handled elsewhere`)
  if (batch.blocked.length) parts.push(`${batch.blocked.length} on hold or not yours to decide`)
  return parts.length ? `Skipped ${parts.join(' and ')}.` : undefined
}

// ---- Denial + archive decisions (dashboard panel) ---------------------------

/** Every denial/archive decision since `sinceIso`, with its reason category.
    The API deliberately omits the free-text reason from this read: an analytics
    consumer has no need for detail a reviewer typed about a document. */
export async function loadDenialDecisionsSince(sinceIso: string): Promise<Decision[]> {
  const out: Decision[] = []
  let cursor: string | undefined
  do {
    const page = await getClient().listDenialDecisions({
      since: sinceIso,
      decision: DENIAL_OUTCOMES as DecisionOutcome[],
      limit: 200,
      cursor,
    })
    out.push(...page.data)
    cursor = page.next_cursor ?? undefined
  } while (cursor)
  return out
}
